import React, { type ReactElement } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Box, Text, Flex, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import Tooltip from '@atlaskit/tooltip';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Skeleton from '@atlaskit/skeleton';
import ErrorIcon from '@atlaskit/icon/core/error';
import { token } from '@atlaskit/tokens';

import {
	useCharlieTable,
	type CharlieTableRow,
	type CharlieTableColumn,
} from '@atlassian/charlie-table';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n';

import { sharedI18n } from '../analyticsShared18n';

import { Table } from './Table';
import { TableSection } from './TableSection';
import { TableRow } from './TableRow';
import { type AnalyticsTableCellSize, TableCell } from './TableCell';
import { getTableUIState, AnalyticsTableUIState, type TableSorting } from './tableUtils';

const i18nBase = defineMessages({
	analyticsTableSortAscIcon: {
		id: 'confluence-analytics.analytics-table.sort-asc-icon',
		defaultMessage: 'Sort in ascending order',
		description:
			'The A11Y label for ascending sorting icon button on any analytics table. These buttons are displayed next to the column titles.',
	},
	analyticsTableSortDescIcon: {
		id: 'confluence-analytics.analytics-table.sort-desc-icon',
		defaultMessage: 'Sort in descending order',
		description:
			'The A11Y label for ascending sorting icon button on any analytics table. These buttons are displayed next to the column titles.',
	},
	analyticsTableEmptyStateMessage: {
		id: 'confluence-analytics.analytics-table.empty-state-message',
		defaultMessage: 'This page hasn’t been viewed since Analytics started tracking activity.',
		description: 'Empty state message displayed on the analytics table, when there are no viewers.',
	},
	analyticsTableNoResultsStateMessage: {
		id: 'confluence-analytics.analytics-table.no-results-state-message',
		defaultMessage: 'No results found',
		description:
			'No results state message displayed on the analytics table, when user does a filtering which returns no match.',
	},
});

const i18nLiveDocs = defineMessages({
	analyticsTableEmptyStateMessage: {
		id: 'confluence-analytics.analytics-table.empty-state-message.livedocs',
		defaultMessage:
			'This page or doc hasn’t been viewed since Analytics started tracking activity.',
		description: 'Updated version of confluence-analytics.analytics-table.empty-state-message',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const inlineTextStyles = xcss({
	display: 'inline',
});

type ColumnHeaderAlignmentType = 'start' | 'center' | 'end' | undefined;

const ColumnHeaderAlignmentWrapper = ({
	alignment,
	children,
}: {
	alignment?: ColumnHeaderAlignmentType;
	children: React.ReactNode;
}) => {
	if (alignment) {
		return <Flex justifyContent={alignment}>{children}</Flex>;
	} else {
		return <>{children}</>;
	}
};

export const AnalyticsTable = <TableDataModel extends object>(props: {
	rows: CharlieTableRow<TableDataModel>[];
	columns: CharlieTableColumn<TableDataModel>[];
	rowsPerPage?: number;
	isLoading?: boolean;
	isFiltering?: boolean;
	hasError?: boolean;
	sortingState?: TableSorting<TableDataModel>;
	onSortChange?: (sortingState: TableSorting<TableDataModel>) => void;
	onRowClick?: (rowData: CharlieTableRow<TableDataModel>, index?: number) => void;
	activeRowIndex?: number;
	// There is a width customization option in CharlieTableColumn but it only accepts numbers.
	// Those numbers, when received back from Charlie's internal state, are somehow transformed.
	// This is a dirty workaround until we have a better understanding how this works, and successfully delegate this to Charlie table.
	getColumnSize?: (column: CharlieTableColumn<TableDataModel>) => AnalyticsTableCellSize;
	getColumnHeaderAlignment?: (
		column: CharlieTableColumn<TableDataModel>,
	) => ColumnHeaderAlignmentType;
	testId?: string;
	ariaLabel?: string;
	emptyComponent?: ReactElement;
}) => {
	const { formatMessage } = useIntl();
	const table = useCharlieTable<TableDataModel>({
		columns: props.columns,
		rows: props.rows,
	});
	const currentUIState = getTableUIState(props, table);

	const tableBodySectionTestId = props.testId && `${props.testId}-section--${currentUIState}`;

	const configIconStyle = (isSortingActive: boolean) => {
		if (currentUIState === AnalyticsTableUIState.Loading) {
			return token('color.icon.subtlest');
		} else if (isSortingActive) {
			return token('color.icon.selected');
		}
		return token('color.icon.subtle');
	};

	return (
		<Table testId={props.testId} aria-label={props.ariaLabel}>
			<TableSection as="thead">
				<TableRow>
					{table.getColumnsInOrder().map((column) => {
						const isDisabled =
							currentUIState === AnalyticsTableUIState.Empty ||
							currentUIState === AnalyticsTableUIState.Loading ||
							currentUIState === AnalyticsTableUIState.Error ||
							currentUIState === AnalyticsTableUIState.NoFilterResults;
						const isSortingActive = !isDisabled && props.sortingState?.field === column.key;

						return (
							<TableCell as="th" key={column.key} size={props.getColumnSize?.(column)}>
								<ColumnHeaderAlignmentWrapper alignment={props.getColumnHeaderAlignment?.(column)}>
									{!column.isSortable && <Text>{column.content}</Text>}

									{column.isSortable && (
										<Tooltip content={column.content}>
											{(tooltipProps) => (
												<Button
													{...tooltipProps}
													isDisabled={isDisabled}
													iconAfter={() =>
														isSortingActive && props.sortingState?.direction === 'asc' ? (
															<ArrowUpIcon
																color={configIconStyle(isSortingActive)}
																label={formatMessage(i18n.analyticsTableSortAscIcon)}
															/>
														) : (
															<ArrowDownIcon
																color={configIconStyle(isSortingActive)}
																label={formatMessage(i18n.analyticsTableSortDescIcon)}
															/>
														)
													}
													appearance="subtle"
													spacing="compact"
													isSelected={isSortingActive}
													onClick={() => {
														props.onSortChange?.({
															field: column.key as Extract<keyof TableDataModel, string>,
															direction: props.sortingState?.direction === 'asc' ? 'desc' : 'asc',
														});
													}}
												>
													{column.content}
												</Button>
											)}
										</Tooltip>
									)}
								</ColumnHeaderAlignmentWrapper>
							</TableCell>
						);
					})}
				</TableRow>
			</TableSection>
			<TableSection as="tbody" testId={tableBodySectionTestId}>
				{currentUIState === AnalyticsTableUIState.Error && (
					<TableRow noBorder>
						<TableCell as="td" size="full">
							<Flex alignItems="center" gap="space.100">
								<ErrorIcon
									label={formatMessage(sharedI18n.serverErrorMessageTitle)}
									color={token('color.icon.warning')}
									spacing="none"
								/>
								<Box xcss={inlineTextStyles}>
									<FormattedMessage {...sharedI18n.serverErrorMessageTitle} />{' '}
									<FormattedMessage {...sharedI18n.serverErrorMessageDescription} />
								</Box>
							</Flex>
						</TableCell>
					</TableRow>
				)}
				{currentUIState === AnalyticsTableUIState.Loading &&
					Array.from({ length: props.rowsPerPage || 5 }).map((_, index) => (
						<TableRow key={index} noBorder={index + 1 === 5}>
							{table.getColumnsInOrder().map((column) => (
								<TableCell as="td" key={column.key} size={props.getColumnSize?.(column)}>
									<Skeleton width="100%" height="16px" isShimmering />
								</TableCell>
							))}
						</TableRow>
					))}
				{currentUIState === AnalyticsTableUIState.NoFilterResults && (
					<TableRow noBorder>
						<TableCell as="td">
							<FormattedMessage {...i18n.analyticsTableNoResultsStateMessage} />
						</TableCell>
					</TableRow>
				)}
				{currentUIState === AnalyticsTableUIState.Empty &&
					(props.emptyComponent || (
						<TableRow noBorder>
							<TableCell as="td">
								<FormattedMessage {...i18n.analyticsTableNoResultsStateMessage} />
							</TableCell>
						</TableRow>
					))}
				{(currentUIState === AnalyticsTableUIState.Data ||
					currentUIState === AnalyticsTableUIState.Filtering) &&
					table.rows.map((row, index) => (
						<TableRow
							key={row.id}
							testId={props.testId && `${props.testId}-row-${index}`}
							noBorder={index + 1 === table.rows.length}
							isSelected={index === props.activeRowIndex}
							isDataRow
							onClick={
								props.onRowClick
									? () => {
											props.onRowClick?.(row, index);
										}
									: undefined
							}
						>
							{table.getColumnsInOrder().map((column) => (
								<TableCell
									as="td"
									key={column.key}
									size={props.getColumnSize?.(column)}
									isSortable={column.isSortable}
								>
									{column.cell.render(row.data)}
								</TableCell>
							))}
						</TableRow>
					))}
			</TableSection>
		</Table>
	);
};
